<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthService } from '@/services/auth.service';
import { useUserRole } from '@/composables/useUserRole';
import { navigationItems, canAccessRoute } from '@/utils/navigation';
import AppIcon from './AppIcon.vue';
defineProps({ workspace: Boolean });
const { t, locale } = useI18n();
const route = useRoute();
const { userProfile, isAuthenticated, login, logoutUser } = useAuthService();
const { userRole } = useUserRole();
const items = computed(() => navigationItems.filter(item => canAccessRoute(item, userRole.value)));
const open = ref(false);
const drawer = ref(null);
const menuButton = ref(null);
const initials = computed(() => (userProfile.value?.name || userProfile.value?.email || 'TS').split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase());
const language = computed({ get: () => locale.value, set: value => { locale.value = value; localStorage.setItem('language', value); } });
const active = item => route.path === item.to || (item.to === '/overview' && route.path.startsWith('/devices/'));
function trapFocus(event) {
  if (!open.value) return;
  if (event.key === 'Escape') { open.value = false; return; }
  if (event.key !== 'Tab') return;
  const elements = [...drawer.value.querySelectorAll('a, button, select')].filter(el => el.getClientRects().length);
  const first = elements[0], last = elements[elements.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
}
watch(open, async value => {
  document.body.classList.toggle('drawer-open', value);
  document.getElementById('main-content')?.toggleAttribute('inert', value);
  document.querySelector('.utility-bar')?.toggleAttribute('inert', value);
  await nextTick();
  if (value) drawer.value.querySelector('button').focus();
  else menuButton.value?.focus();
});
watch(() => route.fullPath, () => { open.value = false; });
const media = window.matchMedia('(min-width: 992px)');
const closeOnDesktop = event => { if (event.matches) open.value = false; };
media.addEventListener('change', closeOnDesktop);
onBeforeUnmount(() => { document.body.classList.remove('drawer-open'); media.removeEventListener('change', closeOnDesktop); });
</script>
<template>
  <header class="utility-bar" :class="{ 'public-bar': !workspace }">
    <div class="utility-start">
      <button v-if="workspace" ref="menuButton" class="btn icon-button mobile-nav-toggle" :aria-label="t('workspace.menu')" :aria-expanded="open" aria-controls="workspace-sidebar" @click="open = true"><AppIcon name="menu" /></button>
      <router-link v-if="!workspace" to="/" class="brand"><img src="@/assets/img/logos/terrasense_2.svg" alt="TerraSense" /></router-link>
      <span v-else class="workspace-label">{{ t('workspace.label') }}</span>
    </div>
    <div class="utility-actions">
      <label class="visually-hidden" for="workspace-language">{{ t('workspace.language') }}</label>
      <select id="workspace-language" v-model="language" class="language-select"><option value="en">EN</option><option value="it">IT</option></select>
      <template v-if="isAuthenticated">
        <router-link class="account-link" to="/profile" :aria-label="t('navbar.profile')"><span class="user-avatar">{{ initials }}</span><span class="account-name">{{ userProfile?.name || userProfile?.email }}<small>{{ t(`role.${userRole}`) }}</small></span></router-link>
        <button class="btn icon-button" :aria-label="t('navbar.log_out')" :title="t('navbar.log_out')" @click="logoutUser"><AppIcon name="logout" /></button>
      </template>
      <button v-else class="btn btn-primary" @click="login()">{{ t('navbar.sign_in') }}</button>
    </div>
  </header>
  <template v-if="workspace">
    <div v-if="open" class="sidebar-backdrop" @click="open = false" />
    <aside id="workspace-sidebar" ref="drawer" class="workspace-sidebar" :class="{ 'is-open': open }" :role="open ? 'dialog' : undefined" :aria-modal="open || undefined" :aria-label="t('workspace.menu')" @keydown="trapFocus">
      <div class="sidebar-brand"><router-link to="/overview" class="brand"><img src="@/assets/img/logos/terrasense_2.svg" alt="TerraSense" /></router-link><button class="btn icon-button mobile-nav-toggle" :aria-label="t('workspace.close')" @click="open = false"><AppIcon name="close" /></button></div>
      <p class="nav-section-label">{{ t('workspace.manage') }}</p>
      <nav :aria-label="t('workspace.menu')"><router-link v-for="item in items" :key="item.to" :to="item.to" class="workspace-nav-link" :class="{ selected: active(item) }" :aria-current="active(item) ? 'page' : undefined"><AppIcon :name="item.icon" /><span>{{ t(item.label) }}</span><AppIcon v-if="active(item)" name="arrow" class="nav-arrow" /></router-link></nav>
      <div class="sidebar-note"><AppIcon name="leaf" /><span>TerraSense<small>{{ t('workspace.connected') }}</small></span></div>
    </aside>
  </template>
</template>
